import "./CreateSubcategory.css";

import { Breadcrumb, Button, Form, Input, message, Select } from "antd";
import { useEffect, useState } from "react";
import { BsTrash } from "react-icons/bs";
import { CgScreen } from "react-icons/cg";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  type CategoryType,
  createSubcategory,
  deleteSubcategory,
  getCategories,
  getPackages,
  getSubcategories,
  type SubCategoryFormType,
  type SubCategoryType,
  updateSubcategory,
  useAppSelector,
} from "../../../_lib/_store";

interface CreateSubCategoryPageProps {
  type: "create" | "edit";
}

export const CreateSubcategoryPage = ({ type }: CreateSubCategoryPageProps) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { isLoading } = useAppSelector((state) => state.subCategory);
  const { isLoading: packageLoading, packages } = useAppSelector(
    (state) => state.package,
  );
  const { isLoading: categoryLoading, categories } = useAppSelector(
    (state) => state.category,
  );
  const { isLoading: subcategoryLoading, subCategories } = useAppSelector(
    (state) => state.subCategory,
  );

  const [filteredCategories, setFilteredCategories] = useState<CategoryType[]>(
    [],
  );
  const [filteredParents, setFilteredParents] = useState<SubCategoryType[]>([]);
  const navigate = useNavigate();
  const { state } = useLocation();

  const isEdit = type === "edit";

  useEffect(() => {
    const subcategoryObj = state?.subcategory;
    if (isEdit && !subcategoryObj) {
      navigate("/subcategory");
    }
    if (isEdit) {
      form.setFieldsValue(subcategoryObj);
      if (subcategoryObj?.packageId) {
        setFilteredCategories(
          categories.filter((c) => c.packageId === subcategoryObj?.packageId),
        );
      }
      if (subcategoryObj?.categoryId) {
        setFilteredParents(
          subCategories.filter(
            (s) => s.categoryId === subcategoryObj.categoryId,
          ),
        );
      }
    }
    getData();
  }, [state]);

  const getData = async () => {
    await getPackages(messageApi);
    await getCategories(messageApi);
    await getSubcategories(messageApi);
  };

  const handleValuesChange = (changedValues: SubCategoryFormType) => {
    if (changedValues.packageId) {
      setFilteredCategories(
        categories.filter((c) => c.packageId === changedValues.packageId),
      );
      form.setFieldsValue({ categoryId: undefined, parentId: undefined });
      setFilteredParents([]);
    }

    if (changedValues.categoryId) {
      setFilteredParents(
        subCategories.filter((s) => s.categoryId === changedValues.categoryId),
      );
      form.setFieldsValue({ parentId: undefined });
    }
  };

  const handleSubmit = async (values: SubCategoryFormType) => {
    const payload = {
      ...values,
      ...(isEdit ? { id: state.subcategory.id } : {}),
    };

    if (isEdit) {
      const res = await updateSubcategory(messageApi, payload);
      if (res) {
        navigate("/subcategory/edit", {
          state: { subcategory: res },
          replace: true,
        });
      }
    } else {
      const res = await createSubcategory(messageApi, payload);
      if (res) {
        form.resetFields();
      }
    }
  };

  const handleDelete = async () => {
    const res = await deleteSubcategory(messageApi, state.package.id);
    if (res) {
      navigate("/subcategory");
    }
  };

  return (
    <>
      {contextHolder}
      <div className="createPackage">
        <Breadcrumb
          items={[
            {
              title: <Link to="/dashboard">Dashboard</Link>,
            },
            {
              title: <Link to="/subcategory">Sub Category</Link>,
            },
            {
              title: isEdit ? "Edit" : "Create new",
            },
          ]}
        />

        {!isEdit ? (
          <h2 className="title">Create new</h2>
        ) : (
          <div className="titleHeader">
            <h2 className="title">Edit</h2>
            <div className="actionButton">
              <Button
                icon={<CgScreen size={17} />}
                size="large"
                onClick={() =>
                  navigate(`/subcategory/show`, {
                    state: { subcategory: state?.subcategory },
                  })
                }
              >
                Show
              </Button>
              <Button
                icon={<BsTrash />}
                size="large"
                onClick={handleDelete}
                loading={isLoading}
              >
                Delete
              </Button>
            </div>
          </div>
        )}

        <div className="form">
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onValuesChange={handleValuesChange}
          >
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Subcategory name is required" },
              ]}
            >
              <Input size="large" />
            </Form.Item>

            <Form.Item label="Description" name="description">
              <Input size="large" />
            </Form.Item>

            <Form.Item
              label="Package Id"
              name="packageId"
              rules={[{ required: true, message: "Package Id is Required" }]}
              help={"This field is required."}
            >
              <Select
                loading={packageLoading}
                size="large"
                options={packages?.map((v) => ({
                  label: v?.name,
                  value: v?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Category Id"
              name="categoryId"
              rules={[{ required: true, message: "Category Id is Required" }]}
              help={
                "Please select a package first to enable category selection and this field is required."
              }
            >
              <Select
                loading={categoryLoading}
                size="large"
                options={filteredCategories.map((c) => ({
                  label: c?.name,
                  value: c?.id,
                }))}
              />
            </Form.Item>

            <Form.Item
              label="Parent Id"
              name="parentId"
              help={
                "Please select a category first to enable subcategory selection."
              }
            >
              <Select
                loading={subcategoryLoading}
                size="large"
                options={filteredParents.map((s) => ({
                  label: s?.name,
                  value: s?.id,
                }))}
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{ width: "150px" }}
                loading={isLoading}
              >
                {isEdit ? "Update" : "Save"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};
